/**
 * Row-visibility rule for the Job Cost Management Report.
 *
 * Lives in its own module purely so it can be unit-tested: importing
 * JobCostReport.tsx in a spec pulls in `@/lib/apiClient`, which runs MSAL at
 * module load and dies with `window is not defined`. The type import below is
 * erased at compile time, so it costs nothing at runtime.
 */
import type { JobCostRow } from '@/lib/apiClient';

/**
 * Should this cost-code row be hidden when "show zero rows" is off?
 *
 * A row hides only when EVERY group cell is empty, not when the TOTALS are: a
 * +$100 Labor estimate offset by a −$100 Material credit nets to a zero total
 * while both cells carry real figures — hiding that row would make the visible
 * rows stop reconciling to the section totals, which still include the amounts
 * (review, #352). All-groups-zero implies totals-zero, so this is strictly
 * stricter. Balance needs no check: it is estimated − actual per cell.
 *
 * ⚠️ COMMITTED COUNTS AS MONEY when its columns are shown (review, #358). A cost
 * code carried ONLY by the committed feed — an open PO against a code with no
 * budget and no spend — has estimated and actual of zero in every group, so
 * without `includeCommitted` it was hidden. That is precisely the unbudgeted
 * commitment the feature was added to surface, and the section and grand totals
 * include it either way, so hiding it also broke the reconciliation this filter
 * exists to protect.
 *
 * When the columns are hidden the row contributes nothing to any visible column,
 * so hiding it is correct — which is why this is a parameter and not a blanket
 * change.
 */
export const isZeroRow = (r: JobCostRow, includeCommitted: boolean): boolean =>
  Object.values(r.byGroup).every((c) =>
    c.estimated === 0 && c.actual === 0 && (!includeCommitted || c.committed === 0));
